import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { CheckCircle2, Circle, ClipboardList, Calendar, RefreshCw, MessageSquare, History, ChevronDown, ChevronUp } from 'lucide-react';

export default function MyTasks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remarkDialog, setRemarkDialog] = useState(null);
  const [remark, setRemark] = useState('');
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const r = await api.get('/employee-tasks/my-today');
      setData(r.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const fetchHistory = async () => {
    if (history.length > 0) { setHistoryOpen(!historyOpen); return; }
    setHistoryLoading(true);
    try {
      const r = await api.get('/employee-tasks/my-history?days=30');
      setHistory(r.data);
      setHistoryOpen(true);
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const toggleTask = async (task, newStatus) => {
    try {
      await api.put('/employee-tasks/update-task', {
        task_id: task.task_id,
        is_completed: newStatus,
        remark: task.remark || '',
      });
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const openRemarkDialog = (task) => {
    setRemarkDialog(task);
    setRemark(task.remark || '');
  };

  const saveRemark = async () => {
    if (!remarkDialog) return;
    try {
      await api.put('/employee-tasks/update-task', {
        task_id: remarkDialog.task_id,
        is_completed: remarkDialog.is_completed,
        remark: remark,
      });
      toast.success('Remark saved');
      setRemarkDialog(null);
      fetchTasks();
    } catch { toast.error('Failed to save remark'); }
  };

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const tasks = data?.tasks || [];
  const totalPct = data?.total_percentage || 0;
  const completedCount = tasks.filter(t => t.is_completed).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">My Daily Tasks</h1>
          <p className="text-slate-500 flex items-center gap-1.5 mt-1">
            <Calendar className="w-4 h-4" /> {today}
          </p>
        </div>
        <Button variant="outline" onClick={() => { setLoading(true); fetchTasks(); }} data-testid="refresh-tasks-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Progress Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Total Tasks</p>
            <p className="text-2xl font-bold">{tasks.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-green-600 mb-1">Completed</p>
            <p className="text-2xl font-bold text-[#138808]">{completedCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-red-500 mb-1">Pending</p>
            <p className="text-2xl font-bold text-red-500">{tasks.length - completedCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 mb-1">Score</p>
            <p className={`text-2xl font-bold ${totalPct >= 100 ? 'text-[#138808]' : totalPct >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
              {totalPct}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Today's Progress</span>
            <span className={`text-sm font-bold ${totalPct >= 100 ? 'text-[#138808]' : 'text-slate-600'}`}>{totalPct}%</span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${totalPct >= 100 ? 'bg-[#138808]' : totalPct >= 50 ? 'bg-[#FF9933]' : 'bg-red-400'}`}
              style={{ width: `${Math.min(totalPct, 100)}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Task List */}
      <Card className="bg-white border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#FF9933]" /> Task Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No tasks assigned for today.</p>
              <p className="text-sm mt-1">Tasks will appear here once your admin assigns them.</p>
            </div>
          ) : (
            <Table data-testid="my-tasks-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold w-12">#</TableHead>
                  <TableHead className="font-semibold">Task</TableHead>
                  <TableHead className="font-semibold w-24">Weight</TableHead>
                  <TableHead className="font-semibold w-24">Status</TableHead>
                  <TableHead className="font-semibold">Remark</TableHead>
                  <TableHead className="font-semibold text-right w-24">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.task_id} className={task.is_completed ? 'bg-green-50/50' : ''} data-testid={`task-item-${task.task_id}`}>
                    <TableCell className="text-slate-500 font-mono">{task.serial_no}</TableCell>
                    <TableCell className={`font-medium ${task.is_completed ? 'line-through text-slate-400' : ''}`}>
                      {task.task_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{task.weightage}%</Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => toggleTask(task, !task.is_completed)}
                        className="flex items-center gap-1.5 cursor-pointer"
                        data-testid={`toggle-complete-${task.task_id}`}
                      >
                        {task.is_completed ? (
                          <CheckCircle2 className="w-5 h-5 text-[#138808]" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300" />
                        )}
                        <span className={`text-xs font-medium ${task.is_completed ? 'text-[#138808]' : 'text-slate-400'}`}>
                          {task.is_completed ? 'Done' : 'Pending'}
                        </span>
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">{task.remark || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openRemarkDialog(task)} className="text-slate-500 hover:text-[#FF9933]" data-testid={`remark-btn-${task.task_id}`}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Task History */}
      <Card className="bg-white border shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={fetchHistory}>
          <CardTitle className="text-lg flex items-center justify-between" data-testid="history-toggle">
            <span className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#FF9933]" /> Past Task History
            </span>
            <span className="flex items-center gap-2 text-sm text-slate-400 font-normal">
              {historyLoading ? 'Loading...' : historyOpen ? 'Hide' : 'Show last 30 days'}
              {historyOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </CardTitle>
        </CardHeader>
        {historyOpen && (
          <CardContent className="pt-4">
            {history.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No past task records found.</p>
            ) : (
              <div className="space-y-2">
                {history.map((day) => {
                  const isExpanded = expandedDate === day.date;
                  const done = day.tasks?.filter(t => t.is_completed).length || 0;
                  const total = day.tasks?.length || 0;
                  return (
                    <div key={day.date} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedDate(isExpanded ? null : day.date)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                        data-testid={`history-row-${day.date}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-sm text-slate-700">{day.date}</span>
                          <span className="text-xs text-slate-500">{done}/{total} tasks</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${day.total_percentage >= 100 ? 'bg-[#138808]' : day.total_percentage >= 50 ? 'bg-[#FF9933]' : 'bg-red-400'}`} style={{ width: `${Math.min(day.total_percentage, 100)}%` }} />
                          </div>
                          <Badge className={day.total_percentage >= 100 ? 'bg-green-100 text-green-700' : day.total_percentage >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}>
                            {day.total_percentage}%
                          </Badge>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </div>
                      </button>
                      {isExpanded && day.tasks?.length > 0 && (
                        <div className="border-t bg-slate-50/50">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-xs w-10">#</TableHead>
                                <TableHead className="text-xs">Task</TableHead>
                                <TableHead className="text-xs w-16">Wt.</TableHead>
                                <TableHead className="text-xs w-20">Status</TableHead>
                                <TableHead className="text-xs">Remark</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {day.tasks.map((t) => (
                                <TableRow key={t.task_id}>
                                  <TableCell className="text-xs text-slate-400">{t.serial_no}</TableCell>
                                  <TableCell className={`text-sm ${t.is_completed ? 'text-slate-400 line-through' : 'font-medium'}`}>{t.task_name}</TableCell>
                                  <TableCell className="text-xs font-mono">{t.weightage}%</TableCell>
                                  <TableCell>
                                    {t.is_completed
                                      ? <span className="flex items-center gap-1 text-xs text-[#138808]"><CheckCircle2 className="w-3.5 h-3.5" />Done</span>
                                      : <span className="flex items-center gap-1 text-xs text-red-500"><Circle className="w-3.5 h-3.5" />Missed</span>
                                    }
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-600">{t.remark || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Remark Dialog */}
      <Dialog open={!!remarkDialog} onOpenChange={(open) => { if (!open) setRemarkDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Remark - {remarkDialog?.task_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={remark} onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter your remark for this task..."
              rows={3}
              className="resize-none"
              data-testid="remark-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemarkDialog(null)}>Cancel</Button>
            <Button onClick={saveRemark} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="save-remark-btn">Save Remark</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
